/**
 * useActiveContext Hook
 *
 * Source of truth: the URL path. After the scope-in-URL refactor
 * (`docs/plan/SCOPE_IN_URL.md`), `activeContext` is **derived** from
 * the route — `/company/*` ⇒ company scope, `/location/:locationId/*`
 * ⇒ location scope keyed by the path param.
 *
 * Dual-mode during rollout: when a legacy `/location/*` route (no
 * `:locationId` param) is still mounted, the hook falls back to the
 * old React state + localStorage path so the app keeps working until
 * every route is migrated. The fallback is removed in Step 8 (cleanup).
 *
 * Mutations: scope changes are navigations, not state updates. Use
 * `useScopeNavigation` to switch — it computes the destination URL
 * (preserving the subpath where it makes sense) and calls
 * `react-router`'s `navigate`. The legacy `setActiveContext` /
 * `clearActiveContext` are kept as no-op-ish shims that delegate to
 * navigation so existing callers don't break mid-rollout.
 *
 * localStorage: kept as a **best-effort hint** for the `/` root
 * redirect and the `/select` gateway pre-selection. It is never
 * authoritative — the URL wins.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, ReactNode } from 'react';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { setActiveLocationIdInternal } from '@/modules/treasury/services/activeContext';

export type ActiveContextType = 'location' | 'company';

export interface ActiveContext {
  type: ActiveContextType;
  id: string;
}

interface ActiveContextValue {
  /** Current active context (location or company), derived from the path. */
  activeContext: ActiveContext | null;
  /** @deprecated use `useScopeNavigation` instead. Kept for compat during rollout. */
  setActiveContext: (ctx: ActiveContext) => void;
  /** @deprecated. Navigation owns the truth now — calling this just records
   *  the legacy hint and (best-effort) navigates to `/select`. */
  clearActiveContext: () => void;
  /** True if the current context is a company view. */
  isCompanyContext: boolean;
  /** True if the current context is a location view. */
  isLocationContext: boolean;
  /** The active location ID (null if company context or no scope). */
  activeLocationId: string | null;
}

const STORAGE_KEY = 'shaka_active_context';

const ActiveContextContext = createContext<ActiveContextValue | undefined>(undefined);

// ── localStorage helpers (best-effort hint, never authoritative) ──────

function readHint(): ActiveContext | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.type && parsed?.id) return parsed as ActiveContext;
    return null;
  } catch {
    return null;
  }
}

function writeHint(ctx: ActiveContext | null): void {
  try {
    if (ctx) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignored: storage can be disabled or full — losing the hint is fine.
  }
}

/** Public helper for the gateway page + root redirect. Returns the
 *  last-seen scope as a hint only. Never call this to render scope-
 *  dependent UI — derive from `useActiveContext()` (path-based) instead. */
export function readLastScopeHint(): ActiveContext | null {
  return readHint();
}

// ── Path → context derivation ─────────────────────────────────────────

// `/location/<uuid>/...` — the scope-in-URL shape. The id is the first
// segment after `/location/` when it looks like a UUID (legacy routes
// like `/location/bookings` carry a module word there instead).
const PATH_LOCATION_ID_RE =
  /^\/location\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i;

export function extractLocationIdFromPath(pathname: string): string | null {
  const match = pathname.match(PATH_LOCATION_ID_RE);
  return match ? match[1] : null;
}

function deriveContextFromPath(
  pathname: string,
  locationIdFromParams: string | undefined,
): ActiveContext | null {
  if (pathname.startsWith('/company')) {
    return { type: 'company', id: 'company' };
  }
  // Path-based location scope (new shape): /location/:locationId/...
  if (locationIdFromParams) {
    return { type: 'location', id: locationIdFromParams };
  }
  // The ActiveContextProvider sits above the routed subtree, so
  // `useParams` usually can't see `:locationId` — but the id is still
  // literally in the path. Extract it directly rather than leaning on
  // the localStorage hint: a navigation that never wrote the hint (e.g.
  // the single-location gateway auto-forward) would otherwise resolve to
  // a null scope and fail every location-scoped permission check.
  const pathLocationId = extractLocationIdFromPath(pathname);
  if (pathLocationId) {
    return { type: 'location', id: pathLocationId };
  }
  // Legacy fallback during rollout: /location/* without a UUID segment.
  // Use the hint so chrome doesn't go blank while routes are being
  // migrated. Removed in Step 8 of the scope-in-URL plan.
  if (pathname.startsWith('/location/')) {
    const hint = readHint();
    if (hint?.type === 'location') return hint;
  }
  return null;
}

// ── Provider ──────────────────────────────────────────────────────────

export function ActiveContextProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // `useParams` only sees `:locationId` when the matched route declares
  // it. During dual-mode rollout most routes still don't, so this is
  // often `undefined` — the derivation falls back to the legacy hint.
  const { locationId: locationIdFromParams } = useParams<{ locationId?: string }>();

  const activeContext = useMemo(
    () => deriveContextFromPath(pathname, locationIdFromParams),
    [pathname, locationIdFromParams],
  );

  // Keep the localStorage hint in sync with the current path so the
  // root redirect / gateway pre-selection reflects the most recent
  // navigation. This is the only write side-effect — reads happen at
  // gateway boundaries, not in render.
  useEffect(() => {
    if (activeContext) writeHint(activeContext);
  }, [activeContext]);

  // Treasury's internal singleton (`setActiveLocationIdInternal`) was
  // a workaround for hooks that couldn't read React context. It still
  // needs to be kept in sync until the treasury module is rewritten
  // to read `useActiveContext()` directly (Step 5 of the plan).
  useEffect(() => {
    if (activeContext?.type === 'location') {
      setActiveLocationIdInternal(activeContext.id);
    } else {
      setActiveLocationIdInternal(undefined);
    }
  }, [activeContext]);

  // ── Legacy shims ────────────────────────────────────────────────
  // Callers of setActiveContext/clearActiveContext aren't migrated yet.
  // Translate them to navigation so they still produce the intended
  // effect (URL drives scope, not state).
  const setActiveContext = useCallback(
    (ctx: ActiveContext) => {
      writeHint(ctx);
      if (ctx.type === 'company') {
        navigate('/company/overview');
      } else {
        // Must land on a route with a registered module segment —
        // `/location/{id}` alone hits LegacyLocationCatchAll which
        // detects the UUID first segment and redirects to /404. The
        // frontdesk overview is the canonical entry point.
        navigate(`/location/${ctx.id}/frontdesk/overview`);
      }
    },
    [navigate],
  );

  const clearActiveContext = useCallback(() => {
    writeHint(null);
    navigate('/select');
  }, [navigate]);

  const value: ActiveContextValue = useMemo(
    () => ({
      activeContext,
      setActiveContext,
      clearActiveContext,
      isCompanyContext: activeContext?.type === 'company',
      isLocationContext: activeContext?.type === 'location',
      activeLocationId: activeContext?.type === 'location' ? activeContext.id : null,
    }),
    [activeContext, setActiveContext, clearActiveContext],
  );

  return React.createElement(ActiveContextContext.Provider, { value }, children);
}

export function useActiveContext(): ActiveContextValue {
  const context = useContext(ActiveContextContext);
  if (!context) {
    throw new Error('useActiveContext must be used within an ActiveContextProvider');
  }
  return context;
}

/**
 * A stable string identifying the active scope, meant to be embedded in a
 * React Query key.
 *
 * Any hook whose fetcher resolves the location at REQUEST time — through
 * `apiClient`'s auto-injected `?location_id=` or through
 * `readActiveLocationId()` — produces different rows per scope while
 * addressing one cache entry. Company scope returns tenant-wide rows into
 * the very same entry a location surface reads. Adding this segment to the
 * key makes the two physically incapable of sharing an entry.
 */
export function useScopeKey(): string {
  const { activeContext } = useActiveContext();
  return activeContext ? `${activeContext.type}:${activeContext.id}` : 'no-scope';
}

// ── New API: useScopeNavigation ───────────────────────────────────────

/**
 * Scope changes that match the spirit of "the URL is the scope".
 *
 *   switchToLocation(id)  — preserves the first subpath segment when
 *                           possible (so switching from
 *                           /location/A/treasury/operations to B lands
 *                           on /location/B/treasury/operations).
 *
 *   switchToCompany()     — goes to /company/overview.
 *
 *   goToScopeRoot()       — drops to the scope-appropriate overview
 *                           regardless of subpath.
 */
export function useScopeNavigation() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return useMemo(
    () => ({
      switchToLocation(locationId: string): void {
        // Path shapes during rollout:
        //   /location/:locationId/treasury/operations  (new)
        //   /location/treasury/operations              (legacy, no id)
        // For "preserve subpath" we strip whichever prefix matches and
        // re-attach the new id.
        let tail = '';
        if (pathname.startsWith('/location/')) {
          const rest = pathname.slice('/location/'.length);
          // /location/:id/tail  → strip the id
          const segments = rest.split('/');
          // Heuristic: if the first segment looks like a uuid, it's
          // already a new-shape URL; drop it.
          const looksLikeId = /^[0-9a-f]{8}-/i.test(segments[0]);
          tail = looksLikeId ? segments.slice(1).join('/') : segments.join('/');
          // Truncate detail ids (e.g. /bookings/123) — keep only the
          // module's overview to avoid 404s when the id doesn't exist
          // in the destination location.
          const tailSegments = tail.split('/').filter(Boolean);
          if (tailSegments.length > 2) {
            tail = tailSegments.slice(0, 2).join('/');
          }
        }
        const dest = tail
          ? `/location/${locationId}/${tail}`
          : `/location/${locationId}/frontdesk/overview`;
        navigate(dest);
      },
      switchToCompany(): void {
        navigate('/company/overview');
      },
      goToScopeRoot(scope: 'company' | 'location', locationId?: string): void {
        if (scope === 'company') {
          navigate('/company/overview');
        } else if (locationId) {
          navigate(`/location/${locationId}/frontdesk/overview`);
        } else {
          navigate('/select');
        }
      },
    }),
    [navigate, pathname],
  );
}
